<script setup lang="ts">
import { computed, h, ref } from 'vue'

const props = withDefaults(defineProps<Props>(), {
  popperPlacement: 'bottom-start',
})

const model = defineModel<Date[]>()

interface Props {
  type: 'daterange' | 'date'
  clearable?: boolean
  disabled?: boolean
  /**
   * Predicate that returns true for dates the user must not be able to
   * pick (greyed out in the calendar). Matches Element Plus's
   * `disabled-date` contract, just pass-through.
   */
  disabledDate?: (date: Date) => boolean
  /**
   * Maximum number of days (inclusive) selectable in a `daterange`.
   * e.g. `31` lets the user pick a start date and up to 30 more days.
   * Once the first date is picked, every day outside that window is
   * greyed out until the range is completed or the picker is closed.
   */
  maxRange?: number
  /**
   * Maximum number of calendar months selectable in a `daterange`,
   * measured from the first-picked date. e.g. `12` lets the user span
   * up to 12 months in either direction from the anchor. Takes effect
   * once the first date is picked; unlike `maxRange` this respects
   * calendar-month boundaries instead of a fixed day count.
   */
  maxRangeMonths?: number
  popperPlacement?:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end'
}

const prefixIcon = h('i', {
  class: 'icon-calendar text-2xl text-text-secondary',
})

const MS_PER_DAY = 24 * 60 * 60 * 1000

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

// First date picked during an in-progress range selection. While set, it
// anchors the allowed window so the user cannot exceed `maxRange` days.
const rangeAnchor = ref<Date | null>(null)

// Element Plus emits `[start, null]` on the first click and `[start, end]`
// on the second. Lock the anchor on the first click, release on the second.
function handleCalendarChange(val: [Date, Date | null]): void {
  if (!props.maxRange && !props.maxRangeMonths)
    return
  rangeAnchor.value = val[1] === null ? val[0] : null
}

// Reset the anchor whenever the selection is confirmed/cleared or the
// dropdown closes, so a fresh selection always starts unconstrained.
function resetAnchor(): void {
  rangeAnchor.value = null
}

function handleVisibleChange(visible: boolean): void {
  if (!visible)
    resetAnchor()
}

// Combine the caller-provided predicate with the in-range window so both
// constraints apply through Element Plus's single `disabled-date` slot.
const effectiveDisabledDate = computed(() => {
  const userPredicate = props.disabledDate
  const maxRange = props.maxRange
  const maxRangeMonths = props.maxRangeMonths

  if (!userPredicate && !maxRange && !maxRangeMonths)
    return undefined

  return (date: Date): boolean => {
    if (userPredicate?.(date))
      return true

    const anchor = rangeAnchor.value
    if (!anchor)
      return false

    const time = startOfDay(date).getTime()

    if (maxRange) {
      const offset = (maxRange - 1) * MS_PER_DAY
      const anchorTime = startOfDay(anchor).getTime()
      if (time < anchorTime - offset || time > anchorTime + offset)
        return true
    }

    if (maxRangeMonths) {
      const lower = startOfDay(
        new Date(anchor.getFullYear(), anchor.getMonth() - maxRangeMonths, anchor.getDate()),
      ).getTime()
      const upper = startOfDay(
        new Date(anchor.getFullYear(), anchor.getMonth() + maxRangeMonths, anchor.getDate()),
      ).getTime()
      if (time < lower || time > upper)
        return true
    }

    return false
  }
})

// Popper options for viewport fitting - works for both mobile and desktop
const popperOptions = computed(() => {
  return {
    placement: props.popperPlacement,
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: 16,
          altAxis: true,
          tether: false,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: [
            'top-start',
            'top-end',
            'bottom-start',
            'bottom-end',
          ],
          boundary: 'viewport',
          padding: 16,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  }
})
</script>

<template>
  <div class="relative">
    <ElDatePicker
      v-model="model"
      :type="type"
      range-separator="-"
      :start-placeholder="
        type === 'daterange' ? $t('start_date') : $t('select_date')
      "
      :show-confirm="type === 'daterange'"
      :end-placeholder="$t('end_date')"
      :placeholder="type === 'date' ? $t('select_date') : undefined"
      class="!h-10 w-full"
      format="DD.MM.YYYY"
      :prefix-icon="prefixIcon"
      :clearable
      :disabled="disabled"
      :disabled-date="effectiveDisabledDate"
      :placement="popperPlacement"
      popper-class="mobile-date-picker"
      :popper-options="popperOptions"
      @calendar-change="handleCalendarChange"
      @change="resetAnchor"
      @visible-change="handleVisibleChange"
    />
  </div>
</template>

<style scoped>
:deep(.el-date-editor) {
  width: 100% !important;
}

:deep(.el-date-editor.el-input__wrapper) {
  width: 100% !important;
  justify-content: flex-start;
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
  gap: 0.5rem;
}

:deep(.el-date-editor .el-range-separator) {
  flex-shrink: 1;
  flex-grow: 0;
  padding: 0;
}

:deep(.el-input__prefix) {
  position: relative;
  left: 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

:deep(.el-input__inner) {
  padding-left: 0 !important;
  padding-right: 0 !important;
  text-align: left;
  flex: 1;
}

:deep(.el-input__suffix) {
  font-size: 20px;
  flex-shrink: 0;
}

:deep(.el-icon.el-input__icon.el-range__close-icon) {
  font-size: 20px !important;
}

/* Mobile input adjustments */
@media (max-width: 640px) {
  :deep(.el-date-editor.el-input__wrapper) {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
    gap: 0.25rem;
  }

  :deep(.el-range-input) {
    font-size: 13px !important;
  }

  :deep(.el-range-separator) {
    font-size: 13px !important;
    padding: 0 2px !important;
  }

  :deep(.el-input__prefix .icon-calendar) {
    font-size: 1.25rem !important;
  }
}
</style>

<!-- Global styles for date picker popper (rendered outside component) -->
<style>
/* Ensure date picker popper stays within viewport */
.mobile-date-picker.el-popper {
  max-width: calc(100vw - 32px) !important;
}

/* Stack calendars vertically on mobile */
@media (max-width: 640px) {
  .mobile-date-picker.el-popper .el-picker-panel__body {
    display: flex !important;
    flex-direction: column !important;
    min-width: unset !important;
    width: 100% !important;
  }

  .mobile-date-picker.el-popper .el-date-range-picker__content {
    display: block !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .mobile-date-picker.el-popper .el-date-range-picker__content.is-right {
    border-left: none !important;
    border-top: 1px solid var(--el-border-color-lighter) !important;
  }

  .mobile-date-picker.el-popper .el-date-table {
    width: 100% !important;
    table-layout: fixed !important;
  }

  .mobile-date-picker.el-popper .el-picker-panel__body-wrapper {
    display: flex !important;
    flex-direction: column !important;
  }

  .mobile-date-picker.el-popper .el-date-range-picker {
    width: calc(100vw - 32px) !important;
  }
}
</style>
